import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo, testimonials, faqs } from '@/mocks/comics';


export default function About() {
  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫画网',
    description: aboutInfo.description,
    url: 'https://www.hanmanhua.com/about',
    about: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      description: aboutInfo.description,
      foundingDate: aboutInfo.founded,
      contactPoint: {
        '@type': 'ContactPoint',
        email: aboutInfo.contact.email,
        contactType: 'customer service',
      },
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫画网 | 海量正版韩漫在线阅读平台"
        description={`${aboutInfo.brandName}${aboutInfo.founded}成立，${aboutInfo.mission}。${aboutInfo.description}`}
        keywords="韩漫画网, 关于我们, 韩漫平台, 正版漫画, 漫画品牌介绍"
        canonicalUrl="https://www.hanmanhua.com/about"
        schemaJson={schemaJson}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: '关于我们' }]} />
              <div className="pb-6">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">关于我们</h1>
                <p className="text-sm text-foreground-500 mt-2">了解韩漫画网的故事与使命</p>
              </div>
            </div>
          </div>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  <strong>{aboutInfo.brandName}</strong>成立于<strong>{aboutInfo.founded}</strong>，
                  是国内领先的韩漫在线阅读平台，致力于为华语读者提供最全面、最优质的韩国漫画资源。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们与多家韩国顶级漫画平台和出版社建立了深度合作关系，确保所有漫画均为正版授权，内容安全可靠。
                  韩漫画网设有<strong>免费漫画弹窗入口</strong>，作为海量正版漫画在线阅读平台，让每一位漫画爱好者都能轻松享受高质量漫画资源。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的使命是<strong>{aboutInfo.mission}</strong>。从热门的恋爱漫画到扣人心弦的悬疑故事，
                  从热血激昂的动作漫画到脑洞大开的奇幻冒险，韩漫画网持续为读者提供丰富多样的漫画选择。
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-[400px]">
                <div className="bg-background-100 rounded-2xl p-8 text-center">
                  <div className="w-20 h-20 mx-auto flex items-center justify-center bg-primary-500 rounded-2xl mb-4">
                    <i className="ri-book-open-line text-3xl text-background-50"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950">{aboutInfo.brandName}</h3>
                  <p className="text-sm text-foreground-500 mt-2">成立于 {aboutInfo.founded}</p>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="bg-background-50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-primary-600">10M+</p>
                      <p className="text-xs text-foreground-500 mt-1">月活跃读者</p>
                    </div>
                    <div className="bg-background-50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-accent-600">5000+</p>
                      <p className="text-xs text-foreground-500 mt-1">正版漫画作品</p>
                    </div>
                    <div className="bg-background-50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-primary-600">99.7%</p>
                      <p className="text-xs text-foreground-500 mt-1">用户满意度</p>
                    </div>
                    <div className="bg-background-50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-accent-600">24h</p>
                      <p className="text-xs text-foreground-500 mt-1">客服响应</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutInfo.features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200 rounded-xl p-6 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {testimonials.map((t, index) => (
                  <div key={index} className="bg-background-50 rounded-xl border border-background-200 p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.comment}&rdquo;</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                      <span className="text-xs text-foreground-400">{t.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.slice(0, 4).map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-all">
                      <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section id="contact" className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
                联系我们
              </h2>
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
                    <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                    <p className="text-xs text-foreground-500 mt-1">{aboutInfo.contact.email}</p>
                  </div>
                  <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
                    <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-wechat-line text-xl"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900">微信公众号</h4>
                    <p className="text-xs text-foreground-500 mt-1">{aboutInfo.contact.wechat}</p>
                  </div>
                  <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
                    <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900">公司地址</h4>
                    <p className="text-xs text-foreground-500 mt-1">{aboutInfo.contact.address}</p>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200 p-6">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4 text-center">发送消息</h3>
                  <form
                    data-readdy-form=""
                    action="https://readdy.ai/api/form/d9otrsiqs38sj6jc7fr0"
                    method="POST"
                    className="space-y-4"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
                      if (honeypot) {
                        const msgEl = document.getElementById('about-form-msg');
                        if (msgEl) { msgEl.textContent = '消息已发送，我们会尽快回复您！'; msgEl.className = 'text-sm text-center mt-4 text-accent-600'; }
                        return;
                      }
                      try {
                        const formData = new FormData(form);
                        formData.delete('website_alt');
                        const res = await fetch(form.action, { method: 'POST', body: new URLSearchParams(formData as unknown as Record<string, string>), headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
                        const text = await res.text();
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        const msgEl = document.getElementById('about-form-msg');
                        if (res.ok && parsed?.code === 'OK') {
                          if (msgEl) { msgEl.textContent = '感谢您的留言，我们会尽快回复！'; msgEl.className = 'text-sm text-center mt-4 text-accent-600'; }
                          form.reset();
                        } else {
                          const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
                          if (msgEl) { msgEl.textContent = errMsg; msgEl.className = 'text-sm text-center mt-4 text-red-500'; }
                        }
                      } catch {
                        const msgEl = document.getElementById('about-form-msg');
                        if (msgEl) { msgEl.textContent = '网络错误，请检查连接后重试'; msgEl.className = 'text-sm text-center mt-4 text-red-500'; }
                      }
                    }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input type="text" id="about-name" name="name" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all text-foreground-700" placeholder="请输入您的姓名" />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input type="email" id="about-email" name="email" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all text-foreground-700" placeholder="请输入您的邮箱" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input type="text" id="about-subject" name="subject" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all text-foreground-700" placeholder="请输入消息主题" />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea id="about-message" name="message" required maxLength={500} rows={4} className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all text-foreground-700 resize-none" placeholder="请输入您的留言内容（500字以内）"></textarea>
                    </div>
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    <button type="submit" className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                      发送消息
                    </button>
                    <div id="about-form-msg"></div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}